import { type FundDetail as FundDetailResponse } from '@d3/contract';

import type { FundDetail } from '../model/fund-detail';

/** 날짜 쌍(nullable 계약 → 뷰모델 [시작, 끝]). null 은 빈 문자열(화면은 빈 칸) */
const pair = (start: string | null, end: string | null): [string, string] => [
  start ?? '',
  end ?? '',
];

/**
 * 응답(@d3/contract FundDetail) → 펀드 상세 뷰모델. 계약 재구성을 흡수한다:
 * id→idx · fundStart/End→fundDuration · investStart/End→investDuration ·
 * fundingRoundMin/Max→fundingRound · investmentTargetComment→commentInvestmentTarget ·
 * 지표(irr · multiple · dpi · 금액 합 등)는 `indicators`(nullable)에서 평면으로 당겨온다 ·
 * targets(indicators.targets)→investGoal · contributions/payments/distributions 평면.
 * indicators.quarterlyValuation→quarterlyValuation(회수 및 평가 총액 그래프). 화면이 쓰지 않는 생성 · 수정 시각은 버린다.
 */
export function toFundDetail(res: FundDetailResponse): FundDetail {
  const i = res.indicators;
  return {
    idx: res.id,
    name: res.name,
    businessNumber: res.businessNumber,
    fundDuration: pair(res.fundStart, res.fundEnd),
    investDuration: pair(res.investStart, res.investEnd),
    fundingRound: pair(res.fundingRoundMin, res.fundingRoundMax),
    commentInvestmentTarget: res.investmentTargetComment ?? '',
    volumeCashCapitalContribution: i?.contribution ?? 0,
    volumeCashCapitalPayment: i?.payment ?? 0,
    volumeCashInvested: i?.invested ?? 0,
    volumeCashUninvested: i?.uninvested ?? 0,
    volumeCashInvestable: res.investableAmount ?? null,
    volumeCashEquivalent: res.cashEquivalent ?? null,
    volumeCashTotalExit: i?.totalExit ?? 0,
    volumeCashTotalValuation: i?.totalValuation ?? 0,
    NumberOfExitComplete: i?.completeExitCount ?? 0,
    NumberOfExitPartial: i?.partialExitCount ?? 0,
    irr1: i?.grossIrr ?? 0,
    irr2: i?.netIrr ?? 0,
    multiple: i?.multiple ?? 0,
    multipleExit: i?.exitMultiple ?? 0,
    dpi: i?.dpi ?? null,
    rvpi: i?.rvpi ?? null,
    tvpi: i?.tvpi ?? null,
    burnrate: i?.burnRate ?? 0,
    investGoal: (i?.targets ?? []).map((t) => ({
      idx: t.targetId,
      name: t.name ?? '',
      volumeTarget: t.goal,
      volumeInvested: t.achieved,
      isMajor: t.isMajor,
    })),
    quarterlyValuation: (i?.quarterlyValuation ?? []).map((q) => ({
      year: q.year,
      quarter: q.quarter,
      amount: q.amount,
    })),
    contribution: res.contributions.map((c) => ({
      idx: c.id,
      lp: { idx: c.lp.id, name: c.lp.name ?? '' },
      volume: c.contribution,
    })),
    distribution: res.distributions.map((d) => ({
      idx: d.id,
      volume: d.amount,
      distributedAt: d.date ?? '',
    })),
    payment: res.payments.map((p) => ({
      idx: p.id,
      volume: p.amount,
      paidAt: p.date ?? '',
    })),
  };
}
